import { useState } from "react";
import type { CognitoUser } from "amazon-cognito-identity-js";

import type { AppConfig } from "../lib/agui";
import {
  completeNewPassword,
  entraConfigured,
  NewPasswordRequired,
  signIn,
  startEntraSignIn,
} from "../lib/auth";
import type { Identity } from "../lib/auth";

interface Props {
  config: AppConfig;
  onSignedIn: (identity: Identity) => void;
}

export default function SignIn({ config, onSignedIn }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  // Cognito forces a password change on first login for admin-created accounts,
  // which is how the demo users are provisioned — so this is the normal path,
  // not an edge case.
  const [challengeUser, setChallengeUser] = useState<CognitoUser | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      if (challengeUser) {
        onSignedIn(await completeNewPassword(challengeUser, email, newPassword));
      } else {
        onSignedIn(await signIn(config, email, password));
      }
    } catch (err) {
      if (err instanceof NewPasswordRequired) {
        setChallengeUser(err.user);
        setError("This account needs a new password before first use.");
      } else {
        setError(err instanceof Error ? err.message : "Sign-in failed.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    // A gold wash at the top of a warm stone page, as on sundialuni.com's hero.
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-sand px-4 py-10"
      style={{
        backgroundImage:
          "linear-gradient(180deg, rgba(233,168,28,0.22) 0%, rgba(233,168,28,0) 320px)",
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-sm bg-white rounded-xl shadow-[0_18px_42px_-28px_rgba(26,23,18,0.35)] border border-line p-6 space-y-4"
      >
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="" width={40} height={40} className="shrink-0" />
          <div>
            <p className="font-display text-base font-bold tracking-tight text-ink">
              Sundial University
            </p>
            {/* Deliberately institution-level, not one agent's name: this form is
                the front door for both the student and the staff assistant. */}
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gold-ink">
              Sundial Assistant
            </p>
          </div>
        </div>

        <p className="text-sm text-ink-muted">
          Sign in with your Sundial account.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-ink">Email</span>
          <input
            type="email"
            required
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
          />
        </label>

        {challengeUser ? (
          <label className="block">
            <span className="text-sm font-medium text-ink">New password</span>
            <input
              type="password"
              required
              minLength={12}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
            />
            <span className="text-xs text-ink-muted mt-1 block">
              At least 12 characters, with upper case, lower case and a digit.
            </span>
          </label>
        ) : (
          <label className="block">
            <span className="text-sm font-medium text-ink">Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-ink focus:border-gold focus:outline-none"
            />
          </label>
        )}

        {error && (
          <p className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        {/* Gold for the student path, ink for staff — the same pairing the public
            site uses for its two login buttons. */}
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-gold text-ink text-sm font-semibold py-2 shadow-[0_10px_22px_-14px_rgba(233,168,28,0.75)] hover:bg-gold-deep disabled:opacity-50"
        >
          {busy ? "Signing in…" : challengeUser ? "Set password and sign in" : "Sign in"}
        </button>

        {/* Staff sign in through Entra; the redirect resolves their identity and
            entitlement server-side, so there is no separate "I am staff" toggle. */}
        {!challengeUser && entraConfigured(config) && (
          <>
            <div className="flex items-center gap-3 text-xs text-ink-faint">
              <span className="h-px flex-1 bg-line" />
              or
              <span className="h-px flex-1 bg-line" />
            </div>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setError(null);
                setBusy(true);
                startEntraSignIn(config).catch((err) => {
                  setError(err instanceof Error ? err.message : "Could not start staff sign-in.");
                  setBusy(false);
                });
              }}
              className="w-full rounded-md bg-ink text-white text-sm font-semibold py-2 hover:bg-[#322c22] disabled:opacity-50"
            >
              Staff sign-in with Microsoft Entra
            </button>
          </>
        )}
      </form>

      <p className="mt-6 max-w-sm text-center text-xs text-ink-faint">
        Sundial University is a fictional institution created for an Amazon Bedrock
        AgentCore demonstration.
      </p>
    </div>
  );
}
